import { AgGridService } from "@topline/services/agGridService";
import moment from "moment";
import { Gen } from "@topline/services/api";
import { ApiClient, MarketClient, ScoringCardsClient } from "@topline/services/apiService";

interface MarketDataResult {
    monthly: {
        value: number[];
        volume: number[];
    };
    yearly: {
        value: number[];
        volume: number[];
    };
}

interface FilterParams {
    page: number;
    pageSize: number;
    pharmacyMarket: string;
    molecules: { name: string }[];
    atc3: string;
    atc4: string;
    nfc3?: { fullName: string }[];
    brand: string;
    sku: string;
    skipSkusIds: number[];
    perscirptionMarket: string;
    sortField: string;
    sortType: string;
    moleculeType: string;
    onlySelectedMolecues: boolean;
}

export class MarketDataProvider {
    static componentName = "MarketDataProvider";
    static $inject = [
        "$q",
        AgGridService.componentName,
        ApiClient.componentName,
        MarketClient.componentName,
        ScoringCardsClient.componentName
    ];

    constructor(
        protected $q: ng.IQService,
        protected agGridService: AgGridService,
        protected apiClient: ApiClient,
        protected marketClient: MarketClient,
        protected scoringCardsClient: ScoringCardsClient
    ) {}

    getBrands(filterParams: FilterParams) {
        return this.apiClient
            .getIMSBrands({
                page: filterParams.page,
                pageSize: filterParams.pageSize,
                pharmacyMarket: filterParams.pharmacyMarket,
                molecules: filterParams.molecules.map(function (x) {
                    return x.name;
                }),
                atc3: filterParams.atc3,
                atc4: filterParams.atc4,
                nfc3: filterParams.nfc3 ? filterParams.nfc3.map(x => x.fullName) : [],
                brand: filterParams.brand,
                sku: filterParams.sku,
                skipSkusIds: filterParams.skipSkusIds,
                perscirptionMarket: filterParams.perscirptionMarket,
                sortField: filterParams.sortField,
                sortType: filterParams.sortType,
                moleculeType: filterParams.moleculeType,
                onlySelectedMolecues: filterParams.onlySelectedMolecues
            })
            .then(function (response) {
                return response;
            });
    }

    getImsProductByMarketFilter = (filters: Gen.MarketFilterDTO[]) => {
        return this.apiClient.getBrandsByFilter({
            filters: filters
        });
    };

    getImsProductByScoringCardsMarketFilter = (filters: Gen.ProjectFilterDTO[]) => {
        return this.scoringCardsClient.getBrandsByFilter({
            filters: filters
        });
    };

    getSkus = (skusSource: string, sourceId: number) => {
        if (skusSource === "marketSource") {
            return this.marketClient.getSkusInMarket(sourceId);
        } else {
            return this.apiClient.getForecastMarketDumpSkus(sourceId);
        }
    };

    createBrandsDataSource = (filterConfig: any) => {
        return this.agGridService.createDataSource(this.getBrands.bind(this), filterConfig);
    };

    loadPharmacyMarkets() {
        return this.apiClient.getPharmacyMarkets();
    }

    loadPerscirptionMarket() {
        return this.apiClient.getPrescriptionMarket();
    }

    findAtc3(searchString: string) {
        return this.apiClient.getAtc3(searchString);
    }

    findMolecule(searchString: string) {
        return this.apiClient.getMolecues(searchString);
    }

    loadMoleculeType() {
        return this.apiClient.getMoleculeType();
    }

    findNfc3(searchString: string, molecules: string[]) {
        return this.apiClient.getNfc3(searchString, molecules);
    }

    loadAtc4(atc3: string) {
        return this.apiClient.getAtc4(undefined, atc3);
    }

    getNewProductList() {
        return this.apiClient.getNewProductList({}).then(function (brands: Gen.BrandInMarketDTO[]) {
            for (const brand of brands) {
                brand.name = brand.name || "N/A";
            }
            return brands;
        });
    }

    getMarketData(filters: Gen.MarketFilterDTO[], startMonth: Date, endMonth: Date): ng.IPromise<MarketDataResult> {
        if (angular.isUndefinedOrNull(filters) || (angular.isArray(filters) && filters.length == 0)) {
            const forecastLength = moment.utc(endMonth).diff(moment.utc(startMonth), "months") + 1;
            const forecastYearsCount = moment.utc(endMonth).diff(moment.utc(startMonth), "years") + 1;

            return this.$q.when({
                monthly: {
                    value: _.times(forecastLength, _.constant(0)),
                    volume: _.times(forecastLength, _.constant(0))
                },
                yearly: {
                    value: _.times(forecastYearsCount, _.constant(0)),
                    volume: _.times(forecastYearsCount, _.constant(0))
                }
            });
        }

        return this.apiClient
            .getMarketValue({
                filters,
                startMonth,
                endMonth
            })
            .then((data: Gen.DataResultDTO) => {
                return {
                    monthly: data,
                    yearly: {
                        volume: this.sumByYear(data.volume, startMonth),
                        value: this.sumByYear(data.value, startMonth)
                    }
                };
            });
    }

    private sumByYear(array: number[], startMonth: Date): number[] {
        const resultArray: number[] = [];

        const groups = _.groupBy(array, (v: number, i: number) => moment.utc(startMonth).add(i, "months").year());

        for (const key of _.chain(groups)
            .keys()
            .sortBy((x: string) => x)
            .value()) {
            resultArray.push(_.reduce(groups[key], (memo: number, num: number) => memo + num, 0));
        }
        return resultArray;
    }
}

angular.module("topline.modules").service(MarketDataProvider.componentName, MarketDataProvider);
